import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI, setAuthToken, clearAuthToken } from './api';
import logger from '@/lib/logger';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const response = await authAPI.me();
      setUser(response.data);
    } catch {
      setUser(null);
      clearAuthToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    setAuthToken(token);
    setUser(userData);
    // Fetch full user profile (includes billing_client_ids, dashboard_layout, etc.)
    try {
      const meRes = await authAPI.me();
      setUser(meRes.data);
    } catch (err) {
      logger.error('Failed to fetch full user profile', err);
    }
    return userData;
  }, []);

  const register = useCallback(async (data) => {
    const response = await authAPI.register(data);
    const { token, user: userData } = response.data;
    setAuthToken(token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      logger.error('Logout request failed', err);
    }
    clearAuthToken();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout, loadUser }), [user, loading, login, register, logout, loadUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
